import { EWithdrawalStep } from "src/types";
import { Bank, v4Service, ozowService, PaymentType, ToggleSwitch, CreateSessionResponse } from "src/api";
import { onlyNumbers, formatNumberToExchange, parseApiError, _t } from "src/utils";
import { useUrlParamsContext } from "src/contexts";

interface UseWithdrawalActionsParams {
  amount: string;
  setAmount: (amount: string) => void;
  setIsLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setCurrentStep: (step: EWithdrawalStep) => void;
  setSessionData: (data: CreateSessionResponse) => void;
  setBanksList: (banks: Bank[]) => void;
  setSelectedBank: (bank: Bank | null) => void;
  setAccountNumber: (account: string) => void;
  sessionData: CreateSessionResponse | null;
  selectedBank: Bank | null;
  accountNumber: string;
  banksList: Bank[];
  currencyId: string;
  currencyPrecision: number;
  mfaCode: string;
  isRtcEnabled: ToggleSwitch | null;
  setIsRtcEnabled: (enabled: ToggleSwitch) => void;
}

export const useWithdrawalActions = (params: UseWithdrawalActionsParams) => {
  const {
    amount,
    setAmount,
    setIsLoading,
    setError,
    setCurrentStep,
    setSessionData,
    setBanksList,
    setSelectedBank,
    setAccountNumber,
    sessionData,
    selectedBank,
    accountNumber,
    banksList,
    currencyId,
    currencyPrecision,
    mfaCode,
    isRtcEnabled,
    setIsRtcEnabled,
  } = params;

  const { walletId, paymentRouteId } = useUrlParamsContext();

  /**
   * Handles amount input change with validation
   */
  const handleAmountChange = (newAmount: string) => {
    if (onlyNumbers(newAmount)) {
      setAmount(formatNumberToExchange(newAmount, currencyPrecision));
    }
  };

  /**
   * Handles bank and account selection
   */
  const handleBankSelection = ({ bank, account }: { bank?: Bank; account?: string }) => {
    if (bank) {
      setSelectedBank(bank);
    }
    if (account !== undefined) {
      setAccountNumber(account);
    }
  };

  /**
   * Handles RTC setting changes
   * Clears banks list so they will be refetched with new RTC filter if user goes back
   */
  const handleRtcChange = (enabled: ToggleSwitch) => {
    // Clear banks list so it will be refetched with new RTC setting
    setBanksList([]);
    // Clear selected bank as it might not be in the new filtered list
    setSelectedBank(null);
    setIsRtcEnabled(enabled);
  };

  /**
   * Proceeds to bank selection step
   * Creates session and fetches available banks (banks list is cached if already loaded)
   */
  const proceedToBankSelection = async () => {
    setIsLoading(true);

    try {
      const session = await v4Service.createSession({
        data: {
          amount: Number(amount),
          payment_route_id: paymentRouteId,
          type: PaymentType.Withdrawal,
        },
      });

      if (!session?.session_id || !session?.exchange) {
        throw new Error(_t("ERRORS.FAILED_TO_CREATE_SESSION"));
      }

      // Only fetch banks list if not already loaded
      if (banksList.length === 0) {
        const banks = await ozowService.getBanksList({
          exchange: session.exchange,
          rtconly: isRtcEnabled === ToggleSwitch.On,
        });
        setBanksList(banks);
      }

      setSessionData(session);
      setCurrentStep(EWithdrawalStep.BankSelection);
    } catch (err) {
      setError(_t("ERRORS.UNABLE_TO_PROCEED"));
      console.error("Error proceeding to bank selection:", err);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Proceeds to confirmation step
   * Simply moves to the next step after bank selection
   */
  const proceedToConfirmation = () => {
    setCurrentStep(EWithdrawalStep.ConfirmWithdrawal);
  };

  /**
   * Initiates the actual withdrawal
   * Creates withdrawal and initiates it with selected bank
   */
  const initiateWithdrawal = async () => {
    if (!sessionData?.session_id || !sessionData?.exchange || !selectedBank) {
      setError(_t("ERRORS.MISSING_REQUIRED_DATA_FOR_WITHDRAWAL"));
      return;
    }

    setIsLoading(true);

    try {
      const paymentData = await v4Service.createWithdrawalFiat({
        amount: Number(amount),
        payment_route_id: paymentRouteId,
        currency_id: currencyId,
        mfa_token: mfaCode || undefined,
        wallet_id: walletId || undefined,
      });

      if (!paymentData?.payment_id) {
        throw new Error(_t("ERRORS.FAILED_TO_CREATE_WITHDRAWAL_PAYMENT"));
      }

      await ozowService.initiateWithdrawal({
        exchange: sessionData.exchange,
        is_rtc: isRtcEnabled === ToggleSwitch.On,
        payment_id: paymentData.payment_id,
        bank_group_id: selectedBank.bankGroupId,
        account_number: accountNumber,
        branch_code: selectedBank.universalBranchCode,
        session_id: sessionData.session_id,
      });

      setCurrentStep(EWithdrawalStep.InitiateWithdrawal);
    } catch (err: any) {
      const errorMessage = parseApiError(err, _t("ERRORS.FAILED_TO_PROCESS_WITHDRAWAL"));
      setError(errorMessage);
      console.error("Error initiating withdrawal:", err);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Main step navigation handler
   * Handles both forward and backward navigation between steps
   * @param targetStep - The step to navigate to
   * @param isBackNavigation - Whether this is a back navigation (skips validation/async logic)
   */
  const handleStepChange = async (targetStep: EWithdrawalStep, isBackNavigation = false) => {
    setError(null);

    if (isBackNavigation) {
      // Simple back navigation - just set the step
      setCurrentStep(targetStep);
      return;
    }

    // Forward navigation with validation/async logic
    switch (targetStep) {
      case EWithdrawalStep.BankSelection:
        await proceedToBankSelection();
        break;
      case EWithdrawalStep.ConfirmWithdrawal:
        proceedToConfirmation();
        break;
      case EWithdrawalStep.InitiateWithdrawal:
        await initiateWithdrawal();
        break;
      default:
        setCurrentStep(targetStep);
    }
  };

  return {
    handleAmountChange,
    handleBankSelection,
    handleStepChange,
    handleRtcChange,
  };
};
